/**
 * Lift2Fit — coaching workspace: the client's programme as a BLOCK.
 *
 * A programme is a block: one start date plus `weeks` repetitions of the same
 * training week. This panel is the coach's authoring desk for it — the client
 * page's "Programme" section — and it reuses the existing builder endpoints with
 * `?clientId=`, never a second write path:
 *
 *   * create the first block for a client who has none (activated on success),
 *   * rename it, change its weeks, its start date and its deload week,
 *   * add / rename / remove / reorder days and their prescriptions,
 *   * activate the block when it is not the client's active programme.
 *
 * Every mutation returns the refreshed programme, so the panel re-renders from
 * the API's own answer and never guesses at the new state.
 */
import { useState } from 'react';
import {
  api,
  type ExerciseGroupType,
  type ProgrammeOverview,
  type ProgrammePlan,
  type ProgrammeRow,
} from '@/lib/api';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Button } from '@/components/ui';
import { CheckIcon, MinusIcon, PlusIcon, SparkIcon, TimerIcon, TrashIcon } from '@/lib/icons';
import {
  CollapsedNote,
  EmptyState,
  LiveMessage,
  LoadingPanel,
  Panel,
  StatusChip,
  groupLetter,
  groupTagText,
  type ChipTone,
} from '../components';
import { buildBlocks, moveWithinCluster, nextGroupNumber } from '../grouping';
import { WarmupCard } from '../warmup';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { formatDateShort, toDateInput } from './shared';

/* ---------------------------------------------------------------------------
   Model helpers
   ------------------------------------------------------------------------ */

interface ClientDay {
  id: string;
  label: string;
  focus: string[];
  rows: ProgrammeRow[];
}

/** The authoritative day list first, falling back to grouping the flat rows. */
function groupClientDays(overview: ProgrammeOverview): ClientDay[] {
  const byDay = (a: ProgrammeRow, b: ProgrammeRow) =>
    a.dayIndex - b.dayIndex || a.orderIndex - b.orderIndex;
  if (overview.days?.length) {
    return overview.days.map((day) => ({
      id: day.id,
      label: day.label,
      focus: day.focus,
      rows: overview.rows.filter((row) => row.dayId === day.id).sort(byDay),
    }));
  }
  const map = new Map<string, ClientDay>();
  for (const row of [...overview.rows].sort(byDay)) {
    const existing = map.get(row.dayId);
    if (existing) existing.rows.push(row);
    else map.set(row.dayId, { id: row.dayId, label: row.dayLabel, focus: row.dayFocus, rows: [row] });
  }
  return [...map.values()];
}

function prescriptionLabel(row: ProgrammeRow): string {
  const reps = row.repMin === row.repMax ? `${row.repMin}` : `${row.repMin}–${row.repMax}`;
  const load = row.targetWeightKg === null ? 'auto load' : `${row.targetWeightKg} kg`;
  const rpe = row.targetRpe === null ? '' : ` @ RPE ${row.targetRpe}`;
  return `${row.prescribedSets} × ${reps} · ${load}${rpe} · rest ${row.restSeconds}s`;
}

/** Move index `from` to `to` inside `list`, returning a new array. */
function move<T>(list: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return next;
  next.splice(to, 0, item);
  return next;
}

/* ---------------------------------------------------------------------------
   Create the first block
   ------------------------------------------------------------------------ */

/* ---------------------------------------------------------------------------
   Edit the block header
   ------------------------------------------------------------------------ */

function EditBlockForm({
  plan,
  clientId,
  onSaved,
  onCancel,
}: {
  plan: ProgrammePlan;
  clientId: string;
  onSaved: (message: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(plan.name);
  const [weeks, setWeeks] = useState(String(plan.weeks));
  const [startsOn, setStartsOn] = useState(plan.startsOn ?? toDateInput(new Date()));
  const [deloadWeek, setDeloadWeek] = useState(plan.deloadWeek === null ? '' : String(plan.deloadWeek));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async () => {
    const weekCount = Number(weeks);
    if (!name.trim()) {
      setMessage('The block needs a name.');
      return;
    }
    if (!Number.isFinite(weekCount) || weekCount < 1 || weekCount > 52) {
      setMessage('Weeks must be between 1 and 52.');
      return;
    }
    const deload = deloadWeek.trim() === '' ? null : Number(deloadWeek);
    if (deload !== null && (!Number.isInteger(deload) || deload < 1 || deload > weekCount)) {
      setMessage('The deload week must fall inside the block.');
      return;
    }
    setBusy(true);
    setMessage(null);
    const result = await api.patchProgrammePlan(
      plan.planId,
      { name: name.trim(), weeks: weekCount, startsOn, deloadWeek: deload },
      clientId,
    );
    setBusy(false);
    if (result.problem || !result.data?.plan) {
      setMessage(result.problem?.detail ?? 'Those block changes did not save.');
      return;
    }
    onSaved('Block updated.');
  };

  return (
    <div className="flex flex-col gap-3 border border-accent bg-page-subtle p-3" data-testid="client-block-edit">
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-[140px] flex-1 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Name</span>
          <input
            value={name}
            data-testid="client-block-edit-name"
            onChange={(event) => setName(event.target.value)}
            className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
          />
        </label>
        <label className="flex w-24 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Weeks</span>
          <input
            type="number"
            min="1"
            max="52"
            value={weeks}
            data-testid="client-block-edit-weeks"
            onChange={(event) => setWeeks(event.target.value)}
            className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
          />
        </label>
        <label className="flex min-w-[130px] flex-1 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Starts</span>
          <input
            type="date"
            value={startsOn}
            data-testid="client-block-edit-start"
            onChange={(event) => setStartsOn(event.target.value)}
            className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
          />
        </label>
        <label className="flex w-28 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Deload week</span>
          <input
            type="number"
            min="1"
            max="52"
            placeholder="none"
            value={deloadWeek}
            data-testid="client-block-edit-deload"
            onChange={(event) => setDeloadWeek(event.target.value)}
            className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} data-testid="client-block-edit-save" onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Save block'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <LiveMessage message={message} tone="danger" />
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Movements
   ------------------------------------------------------------------------ */

function EditExerciseForm({
  row,
  clientId,
  onSaved,
  onCancel,
}: {
  row: ProgrammeRow;
  clientId: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [sets, setSets] = useState(String(row.prescribedSets));
  const [repMin, setRepMin] = useState(String(row.repMin));
  const [repMax, setRepMax] = useState(String(row.repMax));
  const [weight, setWeight] = useState(row.targetWeightKg === null ? '' : String(row.targetWeightKg));
  const [rpe, setRpe] = useState(row.targetRpe === null ? '' : String(row.targetRpe));
  const [rest, setRest] = useState(String(row.restSeconds));
  const [tempo, setTempo] = useState(row.tempo ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setMessage(null);
    const result = await api.patchProgrammeExercise(
      row.planId,
      row.planExerciseId,
      {
        sets: Number(sets) || row.prescribedSets,
        repMin: Number(repMin) || row.repMin,
        repMax: Number(repMax) || row.repMax,
        targetWeightKg: weight.trim() === '' ? null : Number(weight),
        targetRpe: rpe.trim() === '' ? null : Number(rpe),
        restSeconds: Number(rest) || 0,
        tempo: tempo.trim() === '' ? null : tempo.trim(),
        notes: notes.trim() === '' ? null : notes.trim(),
      },
      clientId,
    );
    setBusy(false);
    if (result.problem || !result.data) {
      setMessage(result.problem?.detail ?? 'That prescription did not save.');
      return;
    }
    onSaved();
  };

  const numberField = (label: string, value: string, set: (next: string) => void, testId: string) => (
    <label className="flex min-w-[74px] flex-1 flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        data-testid={testId}
        onChange={(event) => set(event.target.value)}
        className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
      />
    </label>
  );

  return (
    <div className="flex flex-col gap-3 border border-accent bg-page-subtle p-3" data-testid={`client-edit-${row.planExerciseId}`}>
      <div className="flex flex-wrap gap-2">
        {numberField('Sets', sets, setSets, `client-edit-sets-${row.planExerciseId}`)}
        {numberField('Rep min', repMin, setRepMin, `client-edit-repmin-${row.planExerciseId}`)}
        {numberField('Rep max', repMax, setRepMax, `client-edit-repmax-${row.planExerciseId}`)}
        {numberField('Weight kg', weight, setWeight, `client-edit-weight-${row.planExerciseId}`)}
        {numberField('RPE', rpe, setRpe, `client-edit-rpe-${row.planExerciseId}`)}
        {numberField('Rest s', rest, setRest, `client-edit-rest-${row.planExerciseId}`)}
        <label className="flex min-w-[90px] flex-1 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Tempo</span>
          <input
            type="text"
            value={tempo}
            placeholder="3-1-1-0"
            data-testid={`client-edit-tempo-${row.planExerciseId}`}
            onChange={(event) => setTempo(event.target.value)}
            className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Cue</span>
        <input
          type="text"
          value={notes}
          data-testid={`client-edit-notes-${row.planExerciseId}`}
          onChange={(event) => setNotes(event.target.value)}
          className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} data-testid={`client-edit-save-${row.planExerciseId}`} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save prescription'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <LiveMessage message={message} tone="danger" />
    </div>
  );
}

function AddMovementForm({
  planId,
  dayId,
  clientId,
  onSaved,
  onCancel,
}: {
  planId: string;
  dayId: string;
  clientId: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const catalogue = useAsync(() => api.exercises(), []);
  const [exerciseId, setExerciseId] = useState('');
  const [sets, setSets] = useState('3');
  const [repMin, setRepMin] = useState('8');
  const [repMax, setRepMax] = useState('12');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const add = async () => {
    if (!exerciseId) {
      setMessage('Pick a movement first.');
      return;
    }
    setBusy(true);
    setMessage(null);
    const result = await api.addProgrammeExercises(
      planId,
      dayId,
      {
        exerciseId,
        sets: Number(sets) || 3,
        repMin: Number(repMin) || 8,
        repMax: Number(repMax) || 12,
        targetRpe: null,
        restSeconds: 120,
      },
      clientId,
    );
    setBusy(false);
    if (result.problem || !result.data) {
      setMessage(result.problem?.detail ?? 'That movement could not be added.');
      return;
    }
    onSaved();
  };

  return (
    <div className="flex flex-col gap-3 border border-accent bg-page-subtle p-3" data-testid={`client-add-${dayId}`}>
      <label className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Movement</span>
        <select
          value={exerciseId}
          data-testid={`client-add-movement-${dayId}`}
          onChange={(event) => setExerciseId(event.target.value)}
          className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
        >
          <option value="">Choose a movement…</option>
          {(catalogue.data ?? []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </label>
      {catalogue.loading ? <p className="text-[12px] text-ink-muted">Loading the catalogue…</p> : null}
      <div className="flex flex-wrap gap-2">
        <label className="flex w-20 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Sets</span>
          <input type="number" value={sets} onChange={(e) => setSets(e.target.value)} className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink" />
        </label>
        <label className="flex w-20 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Rep min</span>
          <input type="number" value={repMin} onChange={(e) => setRepMin(e.target.value)} className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink" />
        </label>
        <label className="flex w-20 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Rep max</span>
          <input type="number" value={repMax} onChange={(e) => setRepMax(e.target.value)} className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink" />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} data-testid={`client-add-save-${dayId}`} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add to programme'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <LiveMessage message={message} tone="danger" />
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The block panel
   ------------------------------------------------------------------------ */

export function ClientProgrammePanel({
  clientId,
  clientName,
  planId,
  onSelectPlan,
  onNewBlock,
}: {
  clientId: string;
  clientName: string;
  /** The block to show; null means the coach's latest prescribed block. */
  planId?: string | null;
  onSelectPlan?: (planId: string | null) => void;
  onNewBlock?: () => void;
}) {
  const { unit } = useUnitPreference();
  const programmeState = useAsync(
    () => api.programmeOverview(planId ?? null, clientId),
    [clientId, planId ?? 'active'],
  );
  const [editingBlock, setEditingBlock] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingDayId, setAddingDayId] = useState<string | null>(null);
  const [addingDay, setAddingDay] = useState(false);
  const [newDayLabel, setNewDayLabel] = useState('');
  const [renamingDayId, setRenamingDayId] = useState<string | null>(null);
  const [renameLabel, setRenameLabel] = useState('');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [localMessage, setLocalMessage] = useState<{ message: string; tone: ChipTone } | null>(null);

  const overview = programmeState.data;
  const plan = overview?.plan ?? null;
  const days = overview ? groupClientDays(overview) : [];
  const block = plan?.block ?? null;

  const report = (message: string, tone: ChipTone = 'success') => {
    setLocalMessage({ message, tone });
  };

  const reload = () => programmeState.reload();

  if (programmeState.loading && !overview) {
    return <LoadingPanel label="Loading their programme" rows={5} />;
  }

  const removeMovement = async (row: ProgrammeRow) => {
    if (!plan) return;
    setBusy(true);
    const result = await api.deleteProgrammeExercise(plan.planId, row.planExerciseId, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That movement could not be removed.', 'danger');
      return;
    }
    report(`${row.exerciseName} removed from the block.`);
    reload();
  };

  const moveMovement = async (day: ClientDay, row: ProgrammeRow, delta: -1 | 1) => {
    if (!plan) return;
    const dayOrder = moveWithinCluster(day.rows, row.planExerciseId, delta);
    if (!dayOrder) return;
    // The reorder endpoint names the plan's whole prescription set, warm-up
    // included; the warm-up's own order is preserved here.
    const full: string[] = (overview?.warmup?.rows ?? []).map((entry) => entry.planExerciseId);
    for (const entry of days) {
      full.push(...(entry.id === day.id ? dayOrder : entry.rows.map((entryRow) => entryRow.planExerciseId)));
    }
    setBusy(true);
    const result = await api.reorderProgramme(plan.planId, { planExerciseIds: full }, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That reorder did not save.', 'danger');
      return;
    }
    reload();
  };

  /**
   * Grouping parity with the lifter's builder: the same
   * `PATCH /programme/:planId/days/:dayId/group` endpoint through the same
   * `?clientId=` coach scope, so there is one implementation and one read-back.
   */
  const selectedInDay = (day: ClientDay): string[] =>
    day.rows.filter((row) => selectedIds.has(row.planExerciseId)).map((row) => row.planExerciseId);

  const toggleSelected = (planExerciseId: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(planExerciseId)) next.delete(planExerciseId);
      else next.add(planExerciseId);
      return next;
    });
  };

  const groupSelection = async (day: ClientDay, groupType: ExerciseGroupType) => {
    if (!plan) return;
    const ids = selectedInDay(day);
    if (ids.length < 2) {
      report('Select at least two movements to group them — a group of one is not a group.', 'danger');
      return;
    }
    const group = nextGroupNumber(day.rows);
    if (group === null) {
      report('This day already uses all twelve groups.', 'danger');
      return;
    }
    setSelectedIds(new Set());
    setBusy(true);
    const result = await api.groupProgrammeExercises(
      plan.planId,
      day.id,
      { planExerciseIds: ids, group, groupType },
      clientId,
    );
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That group did not save.', 'danger');
      return;
    }
    report(
      `${groupType === 'drop_set' ? 'Drop set' : 'Superset'} ${groupLetter(group)} set on ${ids.length} movements.`,
    );
    reload();
  };

  const ungroupSelection = async (day: ClientDay) => {
    if (!plan) return;
    const ids = selectedInDay(day);
    if (!ids.length) return;
    setSelectedIds(new Set());
    setBusy(true);
    const result = await api.groupProgrammeExercises(plan.planId, day.id, { planExerciseIds: ids, group: null }, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That ungroup did not save.', 'danger');
      return;
    }
    report('Group removed.');
    reload();
  };

  const moveDay = async (index: number, delta: number) => {
    if (!plan) return;
    const dayIds = days.map((day) => day.id);
    const reordered = move(dayIds, index, index + delta);
    if (reordered.join() === dayIds.join()) return;
    setBusy(true);
    const result = await api.reorderProgramme(plan.planId, { dayIds: reordered }, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That reorder did not save.', 'danger');
      return;
    }
    reload();
  };

  const addDay = async () => {
    if (!plan) return;
    const label = newDayLabel.trim() || `Day ${days.length + 1}`;
    setBusy(true);
    const result = await api.addProgrammeDay(plan.planId, { label, focus: [] }, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That day could not be added.', 'danger');
      return;
    }
    setAddingDay(false);
    setNewDayLabel('');
    report(`${label} added to the block.`);
    reload();
  };

  const renameDay = async (day: ClientDay) => {
    if (!plan) return;
    const label = renameLabel.trim();
    if (!label || label === day.label) {
      setRenamingDayId(null);
      return;
    }
    setBusy(true);
    const result = await api.patchProgrammeDay(plan.planId, day.id, { label }, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That day could not be renamed.', 'danger');
      return;
    }
    setRenamingDayId(null);
    report(`${day.label} renamed to ${label}.`);
    reload();
  };

  const removeDay = async (day: ClientDay) => {
    if (!plan) return;
    if (days.length <= 1) {
      report('A block needs at least one training day.', 'danger');
      return;
    }
    setBusy(true);
    const result = await api.deleteProgrammeDay(plan.planId, day.id, clientId);
    setBusy(false);
    if (result.problem || !result.data) {
      report(result.problem?.detail ?? 'That day could not be removed.', 'danger');
      return;
    }
    report(`${day.label} removed from the block.`);
    reload();
  };

  // The panel's state, decided in one place:
  //   none        — no coach prescription at all (the empty state below)
  //   inactive    — a prescription exists but the client runs NOTHING
  //   active      — the shown block is the one the client is running
  //   prescribed  — a prescription exists while the client runs something else
  //                 (their own block; `hasActiveBlock` tells this from `inactive`
  //                 without exposing the client's own block).
  const planRuns = plan?.isActive === true;
  const noActiveBlock = plan !== null && overview?.hasActiveBlock === false;
  const state: 'active' | 'prescribed' | 'inactive' | 'none' = !plan
    ? 'none'
    : noActiveBlock
      ? 'inactive'
      : planRuns
        ? 'active'
        : 'prescribed';

  return (
    <Panel
      testId="crm-client-programme"
      title={
        // D, corrected: the only type label was repeating (title, action row and
        // the block line), so it is gone from the panel entirely — it is the
        // lifter's training type, and it is already on the client's identity
        // header. The block's STATE moves up beside the title instead of sitting
        // next to the block name.
        <span className="flex flex-wrap items-center gap-2">
          Programme
          {state === 'active' ? (
            <span data-testid="client-programme-state" data-state="active">
              <StatusChip tone="success" icon={<CheckIcon />}>
                Active block
              </StatusChip>
            </span>
          ) : state === 'prescribed' ? (
            <span data-testid="client-programme-state" data-state="prescribed">
              <StatusChip tone="warning" icon={<TimerIcon />}>
                Prescribed — not currently active
              </StatusChip>
            </span>
          ) : state === 'inactive' ? (
            <span data-testid="client-programme-state" data-state="inactive">
              <StatusChip tone="neutral" icon={<MinusIcon />}>
                No active block
              </StatusChip>
            </span>
          ) : null}
        </span>
      }
      subtitle={
        plan ? (
          noActiveBlock ? (
            'No block is running for this client right now.'
          ) : (
            // One line under the title: the block's name, then the repeating
            // week's days. No type label (see above).
            <span data-testid="client-programme-block-line" className="flex flex-wrap items-center gap-2">
              <span data-testid="client-programme-name" className="text-[13px] font-bold text-ink">
                {plan.name}
              </span>
              <span data-testid="client-programme-days" className="text-[12px] text-ink-muted">
                {days.length
                  ? days.map((day) => day.label).join(' / ')
                  : `${plan.weeks} week${plan.weeks === 1 ? '' : 's'} block`}
              </span>
            </span>
          )
        ) : (
          'This client has no programme yet. Write their first block here.'
        )
      }
      actions={
        // The panel's action row owns the programme actions: New Block and All
        // Programmes sit beside the block they act on, never in the title row.
        // With no block, the empty state below carries the same two actions.
        plan ? (
          <span className="flex flex-wrap items-center gap-2">
            <Button size="sm" data-testid="crm-client-create-block" onClick={() => onNewBlock?.()}>
              <PlusIcon />
              New Block
            </Button>
            <Link
              to={`/crm/clients/${encodeURIComponent(clientId)}/programmes`}
              data-testid="crm-client-all-programmes"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              All Programmes
            </Link>
          </span>
        ) : null
      }
      bodyClassName="gap-4"
    >
      {programmeState.error ? <LiveMessage message={programmeState.error} tone="danger" /> : null}

      {!plan ? (
        <EmptyState
          title="No programme yet"
          body="Start the client's first block here — copy one of their earlier blocks or begin empty — or open All Programmes to pick a block you wrote for them. The days and prescriptions appear on this page once a block is chosen."
          icon={<SparkIcon className="h-8 w-8 text-accent" />}
          action={
            <>
              <Button data-testid="crm-client-create-block" onClick={() => onNewBlock?.()}>
                <PlusIcon />
                New Block
              </Button>
              <Link
                to={`/crm/clients/${encodeURIComponent(clientId)}/programmes`}
                data-testid="crm-client-all-programmes"
                className="inline-flex h-11 items-center gap-2 border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                All Programmes
              </Link>
            </>
          }
        />
      ) : noActiveBlock ? (
        // Nothing is running: the header says so and carries the two actions;
        // the block details stay out of the way (the All Programmes list holds
        // the history). No block name here, by design.
        <div
          data-testid="client-no-active-block"
          className="flex flex-col gap-2 border border-dashed border-hairline bg-surface p-5"
        >
          <p className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink">No active block</p>
          <p className="max-w-[60ch] text-[14px] text-ink-muted">
            The client is not running a block right now — they deactivated it, or every block has been
            archived. Write their next block with New Block, or open All Programmes to start one.
          </p>
        </div>
      ) : (
        <>
          {planId && !plan.isActive ? (
            <div
              data-testid="client-viewing-block"
              className="flex flex-wrap items-center gap-2 border border-hairline border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[12px] text-ink"
            >
              <span>
                Viewing <span className="font-bold">{plan.name}</span> — not the client’s running block.
              </span>
              <Button size="sm" variant="ghost" onClick={() => onSelectPlan?.(null)}>
                Back to the latest block
              </Button>
            </div>
          ) : null}
          <section
            aria-label="Block dates and review state"
            data-testid="client-block-summary"
            className="flex flex-col gap-3 border border-hairline bg-surface p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              {block?.reviewDue ? (
                <StatusChip tone="info" icon={<TimerIcon />} className="normal-case">
                  <span data-testid="client-block-review" aria-label={block.daysRemaining < 0 ? 'Review overdue' : `Review due, block ends in ${block.daysRemaining} days`}>
                    {block.daysRemaining < 0
                      ? `Review overdue by ${Math.abs(block.daysRemaining)} day(s)`
                      : block.daysRemaining === 0
                        ? 'Review due — block ends today'
                        : `Review due — block ends in ${block.daysRemaining} day(s)`}
                  </span>
                </StatusChip>
              ) : (
                <StatusChip tone="success" icon={<CheckIcon />}>
                  {block ? `${block.daysRemaining} day(s) left` : 'Block running'}
                </StatusChip>
              )}
              {plan.deloadWeek !== null ? <StatusChip tone="neutral">Deload: week {plan.deloadWeek}</StatusChip> : null}
            </div>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {(
                [
                  ['Starts', formatDateShort(plan.startsOn ?? null)],
                  ['Ends', block ? formatDateShort(block.endsOn) : '—'],
                  ['Week', block ? `${block.weekOf} of ${block.weeks}` : '—'],
                  ['Weeks', String(plan.weeks)],
                  ['Days / week', String(days.length)],
                  ['Deload', plan.deloadWeek === null ? 'None' : `Week ${plan.deloadWeek}`],
                ] as Array<[string, string]>
              ).map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1 border border-hairline bg-page p-3">
                  <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</dt>
                  <dd className="l2f-tabular font-mono text-[14px] font-bold text-ink">{value}</dd>
                </div>
              ))}
            </dl>

            <p className="text-[12px] text-ink-muted">
              A block is {plan.weeks} × the repeating week below
              {block ? `, ending ${formatDateShort(block.endsOn)}` : ''}. Every week runs the same days; change the
              block once and the whole block follows.
            </p>

            {editingBlock ? (
              <EditBlockForm
                plan={plan}
                clientId={clientId}
                onCancel={() => setEditingBlock(false)}
                onSaved={(message) => {
                  setEditingBlock(false);
                  report(message);
                  reload();
                }}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" data-testid="client-block-edit-open" onClick={() => setEditingBlock(true)}>
                  Edit block
                </Button>
                <Button size="sm" variant="outline" data-testid="client-day-add-open" onClick={() => setAddingDay(true)}>
                  <PlusIcon />
                  Add day
                </Button>
              </div>
            )}

            {addingDay ? (
              <div className="flex flex-wrap items-end gap-2 border border-accent bg-page-subtle p-3" data-testid="client-day-add">
                <label className="flex min-w-[160px] flex-1 flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">New day label</span>
                  <input
                    value={newDayLabel}
                    data-testid="client-day-add-label"
                    onChange={(event) => setNewDayLabel(event.target.value)}
                    className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[14px] text-ink"
                  />
                </label>
                <Button size="sm" disabled={busy} data-testid="client-day-add-submit" onClick={() => void addDay()}>
                  Add day
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAddingDay(false)}>
                  Cancel
                </Button>
              </div>
            ) : null}
          </section>

          {/* The shared warm-up (0019) — the coach's card is the lifter's card. */}
          <WarmupCard
            warmup={overview?.warmup ?? null}
            planId={plan.planId}
            clientId={clientId}
            editable
            unit={unit}
            busy={busy}
            otherPlanExerciseIds={days.flatMap((day) => day.rows.map((row) => row.planExerciseId))}
            audience="coach"
            testId="client-warmup-card"
            onApplied={(result, message) => {
              if (result.problem || !result.data) {
                report(result.problem?.detail ?? 'That warm-up change did not save.', 'danger');
                return;
              }
              report(message);
              reload();
            }}
          />

          {days.map((day, dayIndex) => (
            <section
              key={day.id}
              className="flex flex-col gap-3 border border-hairline bg-surface p-4"
              data-testid={`client-day-${day.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                {renamingDayId === day.id ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <input
                      value={renameLabel}
                      data-testid={`client-day-rename-input-${day.id}`}
                      onChange={(event) => setRenameLabel(event.target.value)}
                      className="h-10 min-w-[160px] border border-hairline bg-page px-2 text-[14px] text-ink"
                    />
                    <Button size="sm" disabled={busy} data-testid={`client-day-rename-save-${day.id}`} onClick={() => void renameDay(day)}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRenamingDayId(null)}>
                      Cancel
                    </Button>
                  </span>
                ) : (
                  <h3 className="text-[14px] font-bold uppercase tracking-[0.06em] text-ink">
                    {dayIndex + 1}. {day.label}
                    {day.focus.length ? (
                      <span className="ml-2 text-[12px] font-normal text-ink-muted">{day.focus.join(' · ')}</span>
                    ) : null}
                  </h3>
                )}
                <span className="flex flex-wrap items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Move ${day.label} earlier`}
                    disabled={busy || dayIndex === 0}
                    data-testid={`client-day-move-up-${day.id}`}
                    onClick={() => void moveDay(dayIndex, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Move ${day.label} later`}
                    disabled={busy || dayIndex === days.length - 1}
                    data-testid={`client-day-move-down-${day.id}`}
                    onClick={() => void moveDay(dayIndex, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`client-day-rename-${day.id}`}
                    onClick={() => {
                      setRenamingDayId(day.id);
                      setRenameLabel(day.label);
                    }}
                  >
                    Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`client-add-open-${day.id}`}
                    onClick={() => {
                      setAddingDayId(day.id);
                      setEditingId(null);
                    }}
                  >
                    <PlusIcon />
                    Add movement
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${day.label}`}
                    disabled={busy}
                    data-testid={`client-day-delete-${day.id}`}
                    onClick={() => void removeDay(day)}
                  >
                    <TrashIcon />
                  </Button>
                </span>
              </div>

              {day.rows.length ? (
                <>
                  {selectedInDay(day).length ? (
                    <div
                      data-testid={`client-group-actions-${day.id}`}
                      className="flex flex-wrap items-center gap-2 border border-accent bg-page-subtle px-3 py-2"
                    >
                      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink">
                        {selectedInDay(day).length} selected
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || selectedInDay(day).length < 2}
                        data-testid={`client-group-superset-${day.id}`}
                        onClick={() => void groupSelection(day, 'superset')}
                      >
                        Group as superset
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || selectedInDay(day).length < 2}
                        data-testid={`client-group-drop-set-${day.id}`}
                        onClick={() => void groupSelection(day, 'drop_set')}
                      >
                        Group as drop set
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        data-testid={`client-ungroup-${day.id}`}
                        onClick={() => void ungroupSelection(day)}
                      >
                        Ungroup
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`client-clear-selection-${day.id}`}
                        onClick={() => setSelectedIds(new Set())}
                      >
                        Clear
                      </Button>
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-2">
                    {buildBlocks(day.rows).map((block, blockIndex) => {
                      const isGroup = block.rows.length > 1;
                      return (
                        <div key={`${day.id}-${block.group ?? 'solo'}-${blockIndex}`} className="flex flex-col">
                          {isGroup ? (
                            <div
                              data-testid={`client-group-tag-${day.id}-${block.group}`}
                              className="flex items-center gap-2 border-b border-hairline bg-page-subtle px-2 py-1.5"
                            >
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-black text-on-accent">
                                {groupLetter(block.group)}
                              </span>
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent">
                                {groupTagText(block.group, block.groupType)} —{' '}
                                {block.groupType === 'drop_set' ? 'same movement, descending load' : 'performed back to back'}
                              </span>
                            </div>
                          ) : null}

                          <ul className="flex flex-col">
                            {block.rows.map((row) => {
                              const canMoveUp = moveWithinCluster(day.rows, row.planExerciseId, -1) !== null;
                              const canMoveDown = moveWithinCluster(day.rows, row.planExerciseId, 1) !== null;
                              const checked = selectedIds.has(row.planExerciseId);
                              return (
                                <li
                                  key={row.planExerciseId}
                                  className={`flex flex-col gap-2 border-b border-hairline px-2 py-2 last:border-b-0 ${checked ? 'bg-accent/[0.04]' : ''}`}
                                >
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                      <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          data-testid={`client-select-${row.planExerciseId}`}
                                          aria-label={`Select ${row.exerciseName} for grouping`}
                                          onChange={() => toggleSelected(row.planExerciseId)}
                                          className="h-4 w-4 shrink-0 border border-hairline accent-[color:var(--l2f-accent)]"
                                        />
                                        {row.exerciseName}
                                        {row.supersetGroup !== null ? (
                                          <StatusChip tone="info">
                                            {groupTagText(row.supersetGroup, row.groupType)}
                                          </StatusChip>
                                        ) : null}
                                      </span>
                                      <span className="l2f-tabular font-mono text-[12px] text-ink-muted">{prescriptionLabel(row)}</span>
                                      {row.tempo ? <span className="text-[12px] text-ink-muted">tempo {row.tempo}</span> : null}
                                      {unit === 'imperial' && row.targetWeightKg !== null ? (
                                        <span className="text-[12px] text-ink-muted">
                                          ≈ {Math.round(row.targetWeightKg * 2.20462)} lb
                                        </span>
                                      ) : null}
                                      <CollapsedNote id={`client-${row.planExerciseId}`} note={row.notes} />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={`Move ${row.exerciseName} earlier`}
                                        disabled={busy || !canMoveUp}
                                        data-testid={`client-row-move-up-${row.planExerciseId}`}
                                        onClick={() => void moveMovement(day, row, -1)}
                                      >
                                        ↑
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={`Move ${row.exerciseName} later`}
                                        disabled={busy || !canMoveDown}
                                        data-testid={`client-row-move-down-${row.planExerciseId}`}
                                        onClick={() => void moveMovement(day, row, 1)}
                                      >
                                        ↓
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid={`client-edit-open-${row.planExerciseId}`}
                                        onClick={() => {
                                          setEditingId(row.planExerciseId);
                                          setAddingDayId(null);
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        data-testid={`client-delete-${row.planExerciseId}`}
                                        onClick={() => void removeMovement(row)}
                                      >
                                        <TrashIcon />
                                        Remove
                                      </Button>
                                    </div>
                                  </div>
                                  {editingId === row.planExerciseId ? (
                                    <EditExerciseForm
                                      row={row}
                                      clientId={clientId}
                                      onCancel={() => setEditingId(null)}
                                      onSaved={() => {
                                        setEditingId(null);
                                        report(`${row.exerciseName} updated.`);
                                        reload();
                                      }}
                                    />
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="flex items-center gap-2 text-[13px] text-ink-muted">
                  <MinusIcon />
                  No movements on this day yet.
                </p>
              )}

              {addingDayId === day.id ? (
                <AddMovementForm
                  planId={plan.planId}
                  dayId={day.id}
                  clientId={clientId}
                  onCancel={() => setAddingDayId(null)}
                  onSaved={() => {
                    setAddingDayId(null);
                    report(`Movement added to ${day.label}.`);
                    reload();
                  }}
                />
              ) : null}
            </section>
          ))}

          {!days.length ? (
            <EmptyState
              title="No training days yet"
              body="Add the first day of the repeating week; the block repeats it every week."
              icon={<SparkIcon className="h-8 w-8 text-accent" />}
              action={
                <Button data-testid="client-day-add-empty" onClick={() => setAddingDay(true)}>
                  <PlusIcon />
                  Add day
                </Button>
              }
            />
          ) : null}
        </>
      )}

      {localMessage ? <LiveMessage message={localMessage.message} tone={localMessage.tone} /> : null}
    </Panel>
  );
}
